import * as buttons from 'smg/buttons';
import * as phone from 'smg/phone';
import * as template from 'smg/template';

import axios from 'smg/axios';
import { CurrentUser, UpdateCurrentUser, UpdateCurrentUserPassword } from 'smg/customer-api/currentuser';
import * as models from 'smg/customer-api/models';
import { ClearCache } from 'smg/customer-api/session';
import { GetAxiosParamErrors, ShowError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import Modal from 'smg/modal';
import { GetFirstAndLast } from 'smg/users';
import { AccountDetailsInit, RequireSession } from '../../account';
import i18n from '../../i18n';

// How long a "Saved" chip stays up on a control that saves on the spot.
const savedChipMs = 2500;

// How we should reach you: the radio value beside the flag a rep reads on the
// customer's record. One table, so loading, saving and clearing cannot
// disagree about which flag means what.
const reachOptions: { value: string; flag: models.FlagID }[] = [
    { value: 'email', flag: models.FlagID.FLAG_PREFERS_EMAIL },
    { value: 'text', flag: models.FlagID.FLAG_PREFERS_SMS },
    { value: 'call', flag: models.FlagID.FLAG_PREFERS_CALLING },
];

const confirmModalOptions = {
    width: '38.5rem',
    confirmText: 'OK',
    appendConfirmButtonClass: 'w-100 w-sm-auto btn-sm-lg',
    cancelButton: false as const,
    appendClassBySelector: {
        '.modal-dialog': 'px-4 px-6',
        '.modal-body': 'px-6 pt-8 pb-2',
        '.modal-footer': 'p-6 pt-sm-10 pb-sm-14 justify-content-center',
    },
};

void main();

async function main() {
    if (await RequireSession()) {
        return;
    }

    await AccountDetailsInit();

    const $lede = document.querySelector('#profile-lede') as HTMLElement;
    const $ledeEmail = document.querySelector('#profile-lede-email') as HTMLElement;
    const $loading = document.querySelector('#profile-loading') as HTMLElement;
    const $loadFailed = document.querySelector('#profile-load-failed') as HTMLElement;
    const $retry = document.querySelector('#profile-retry') as HTMLButtonElement;

    const $read = document.querySelector('#profile-read') as HTMLElement;
    const $readName = document.querySelector('#profile-read-name') as HTMLElement;
    const $readOrg = document.querySelector('#profile-read-org') as HTMLElement;
    const $readEmail = document.querySelector('#profile-read-email') as HTMLElement;
    const $readPhone = document.querySelector('#profile-read-phone') as HTMLElement;
    const $editOpen = document.querySelector('#profile-edit-open') as HTMLButtonElement;

    const $edit = document.querySelector('#profile-edit') as HTMLElement;
    const $editForm = document.querySelector('#profile-edit-form') as HTMLFormElement;
    const $editAlert = document.querySelector('#profile-edit-alert') as HTMLElement;
    const $editAlertText = document.querySelector('#profile-edit-alert-text') as HTMLElement;
    const $editName = document.querySelector('#profile-edit-name') as HTMLInputElement;
    const $editOrg = document.querySelector('#profile-edit-org') as HTMLInputElement;
    const $editEmail = document.querySelector('#profile-edit-email') as HTMLInputElement;
    const $editPhone = document.querySelector('#profile-edit-phone') as HTMLInputElement;
    const $editSubmit = document.querySelector('#profile-edit-submit') as HTMLButtonElement;
    const $editCancel = document.querySelector('#profile-edit-cancel') as HTMLButtonElement;
    const $stored = document.querySelector('#profile-phone-stored') as HTMLElement;
    const $storedValue = document.querySelector('#profile-phone-stored-value') as HTMLElement;

    const $texts = document.querySelector('#profile-texts') as HTMLInputElement;
    const $textsNoNumber = document.querySelector('#profile-texts-no-number') as HTMLElement;

    const $reachRadios = Array.from(document.querySelectorAll('input[name="profile-reach"]') as NodeListOf<HTMLInputElement>);
    const $reachNone = document.querySelector('#profile-reach-none') as HTMLElement;

    const $passwordForm = document.querySelector('#profile-password-form') as HTMLFormElement;
    const $passwordAlert = document.querySelector('#profile-password-alert') as HTMLElement;
    const $passwordAlertText = document.querySelector('#profile-password-alert-text') as HTMLElement;
    const $passwordNew = document.querySelector('#profile-password-new') as HTMLInputElement;
    const $passwordConfirm = document.querySelector('#profile-password-confirm') as HTMLInputElement;
    const $passwordSubmit = document.querySelector('#profile-password-submit') as HTMLButtonElement;

    const editPhone = new phone.Phone($editPhone);

    // The one record of what the server holds. Cancelling an edit re-renders
    // from it rather than re-fetching, so leaving the form cannot show a value
    // the account does not have.
    let user = new models.User();
    let prefs = new models.NotificationPreferences();

    const setValue = ($el: HTMLElement, value: string, emptyLabel: string) => {
        $el.textContent = value || emptyLabel;
        $el.classList.toggle('profile-value-empty', !value);
    };

    const renderRead = () => {
        setValue($readName, user.fullName(), '');
        setValue($readOrg, user.company, i18n('profileEmptyOrg'));
        setValue($readEmail, user.email, '');
        setValue($readPhone, phone.Human(user.phone), i18n('profileEmptyPhone'));
        $ledeEmail.textContent = user.email;
        $lede.classList.remove('d-none');
    };

    const renderEditFields = () => {
        $editName.value = user.fullName();
        $editOrg.value = user.company;
        $editEmail.value = user.email;
        void editPhone.iti.promise.then(() => {
            editPhone.iti.setNumber(user.phone);
            renderStored();
        });
    };

    // The international form the account will store, under the field, so a
    // customer can see what their own digits become.
    const renderStored = () => {
        const stored = editPhone.iti.getNumber();
        $storedValue.textContent = stored;
        $stored.classList.toggle('d-none', !stored);
    };

    const renderTexts = () => {
        $texts.checked = prefs.textMessages.enabled;
        // On with no number is the broken state it is: the choice is worth
        // keeping, but nothing can be delivered until a number is on file.
        $textsNoNumber.classList.toggle('d-none', !($texts.checked && !user.phone));
    };

    const toRead = () => {
        $read.classList.remove('d-none');
        $edit.classList.add('d-none');
        $editAlert.classList.add('d-none');
        renderRead();
    };

    // A load that does not land says so and offers another go: the spinner is
    // not a state anyone can leave.
    const load = async () => {
        $loadFailed.classList.add('d-none');
        $loading.classList.remove('d-none');
        await CurrentUser().then(resp => {
            user = resp.user;
            prefs = resp.notificationPreferences;
            $loading.classList.add('d-none');
            // toRead rather than renderRead: reading is the state a load lands
            // in, and it is the one place that decides which of read/edit shows.
            toRead();
            renderEditFields();
            renderTexts();
        }).catch(err => {
            $loading.classList.add('d-none');
            $loadFailed.classList.remove('d-none');
            ShowError(err);
        });
    };

    const flags = await axios.get('/users/flags').
        then(resp => models.UserFlagsResponse.fromJSON(resp.data).flagIDs).
        catch(err => {
            ShowError(err);
            return [] as models.FlagID[];
        });

    // Anything but exactly one of these held is what a rep reads as "no
    // preference" — none chosen, or a legacy row carrying more than one.
    const held = reachOptions.filter(option => flags.includes(option.flag));
    if (held.length === 1) {
        $reachRadios.forEach($r => $r.checked = $r.value === held[0].value);
    } else {
        $reachNone.classList.remove('d-none');
    }

    $retry.addEventListener('click', () => {
        ClearCache();
        void load();
    });

    ClearCache();
    await load();

    // A control that saves the moment it is used: inert while the write is in
    // flight, a chip for each outcome, and the previous position restored when
    // the write does not land.
    const selfSaving = (name: string, controls: HTMLInputElement[], save: () => Promise<unknown>, revert: () => void) => {
        const $saving = document.querySelector(`#profile-${name}-saving`) as HTMLElement;
        const $saved = document.querySelector(`#profile-${name}-saved`) as HTMLElement;
        const $failed = document.querySelector(`#profile-${name}-failed`) as HTMLElement;
        let savedTimer = 0;

        return async () => {
            window.clearTimeout(savedTimer);
            $saved.classList.add('d-none');
            $failed.classList.add('d-none');
            $saving.classList.remove('d-none');
            controls.forEach($c => $c.disabled = true);

            await save().then(() => {
                $saving.classList.add('d-none');
                $saved.classList.remove('d-none');
                savedTimer = window.setTimeout(() => $saved.classList.add('d-none'), savedChipMs);
            }).catch(() => {
                $saving.classList.add('d-none');
                $failed.classList.remove('d-none');
                revert();
            }).finally(() => {
                controls.forEach($c => $c.disabled = false);
            });
        };
    };

    const saveTexts = selfSaving('texts', [$texts], () => {
        const state = $texts.checked ?
            models.TextMessagesStateValue.TEXT_MESSAGES_STATE_ON :
            models.TextMessagesStateValue.TEXT_MESSAGES_STATE_OFF;
        return UpdateCurrentUser({
            notificationPreferences: { textMessages: { state } },
        }).then(resp => {
            prefs = resp.notificationPreferences;
            renderTexts();
        });
    }, () => {
        renderTexts();
    });

    $texts.addEventListener('change', () => {
        // The notice follows the switch's new position at once; the save either
        // confirms it or puts the switch back.
        $textsNoNumber.classList.toggle('d-none', !($texts.checked && !user.phone));
        void saveTexts();
    });

    const saveReach = selfSaving('reach', $reachRadios, async () => {
        const picked = reachOptions.find(option => option.value === $reachRadios.find($r => $r.checked)?.value);
        if (picked === undefined) {
            return;
        }
        await axios.post(`/flags/${picked.flag}`);
        // The preference is one choice, so the other two go: a rep seeing two
        // at once learns nothing from either.
        await Promise.all(reachOptions.
            filter(option => option !== picked).
            map(option => axios.delete(`/flags/${option.flag}`)));
    }, () => {
        $reachRadios.forEach($r => $r.checked = false);
        $reachNone.classList.remove('d-none');
    });

    $reachRadios.forEach($r => $r.addEventListener('change', () => {
        $reachNone.classList.add('d-none');
        void saveReach();
    }));

    $editOpen.addEventListener('click', () => {
        $read.classList.add('d-none');
        $edit.classList.remove('d-none');
        renderEditFields();
        $editName.focus();
        $editName.setSelectionRange(0, $editName.value.length);
    });

    $editCancel.addEventListener('click', toRead);

    $editPhone.addEventListener('input', renderStored);
    $editPhone.addEventListener('change', renderStored);

    // The server names the field and, for the one failure a message cannot be
    // trusted to distinguish, the code (EMAIL_IN_USE, #10148). Both the notice
    // above the form and the field itself carry the same sentence.
    const identityFailure = (err: unknown): { message: string; $field?: HTMLInputElement } => {
        for (const p of GetAxiosParamErrors(err)?.errors ?? []) {
            if (p.code === 'EMAIL_IN_USE') {
                return { message: i18n('profileErrorEmailTaken'), $field: $editEmail };
            }
            if (p.param === 'email') {
                return { message: i18n('profileErrorEmailInvalid'), $field: $editEmail };
            }
            if (p.param === 'phone') {
                return { message: i18n('profileErrorPhoneInvalid'), $field: $editPhone };
            }
        }
        return { message: i18n('profileErrorGeneric') };
    };

    $editForm.noValidate = true;
    $editForm.addEventListener('submit', async e => {
        e.preventDefault();

        $editAlert.classList.add('d-none');
        if (!ReportValidity($editForm)) {
            $editAlertText.textContent = i18n('profileErrorRequired');
            $editAlert.classList.remove('d-none');
            return;
        }

        const fields = [$editName, $editOrg, $editEmail, $editPhone];
        buttons.Start($editSubmit);
        fields.forEach($f => $f.disabled = true);

        const { firstName, lastName } = GetFirstAndLast($editName.value);
        await UpdateCurrentUser({
            firstName,
            lastName,
            company: $editOrg.value,
            email: $editEmail.value,
            phone: await editPhone.format(),
        }).then(resp => {
            user = resp.user;
            prefs = resp.notificationPreferences;
            renderTexts();
            new Modal({
                ...confirmModalOptions,
                body: template.Clone('#profile-saved'),
                onClose: toRead,
                onSubmit: toRead,
            });
        }).catch(err => {
            const failure = identityFailure(err);
            $editAlertText.textContent = failure.message;
            $editAlert.classList.remove('d-none');
            if (failure.$field) {
                failure.$field.setCustomValidity(failure.message);
                failure.$field.addEventListener('input', () => failure.$field?.setCustomValidity(''), { once: true });
                ReportValidity($editForm);
            }
        }).finally(() => {
            buttons.Stop($editSubmit);
            fields.forEach($f => $f.disabled = false);
        });
    });

    $passwordForm.noValidate = true;
    $passwordForm.addEventListener('submit', async e => {
        e.preventDefault();

        $passwordAlert.classList.add('d-none');
        $passwordNew.setCustomValidity($passwordNew.value.length && $passwordNew.value.length < 6 ? i18n('profilePasswordTooShort') : '');
        $passwordConfirm.setCustomValidity($passwordNew.value === $passwordConfirm.value ? '' : i18n('profilePasswordMismatch'));

        if (!ReportValidity($passwordForm)) {
            return;
        }

        buttons.Start($passwordSubmit);

        await UpdateCurrentUserPassword({
            newPassword: $passwordNew.value,
            confirmPassword: $passwordConfirm.value,
        }).then(() => {
            $passwordNew.value = '';
            $passwordConfirm.value = '';
            new Modal({
                ...confirmModalOptions,
                body: template.Clone('#profile-password-saved'),
            });
        }).catch(() => {
            $passwordAlertText.textContent = i18n('profilePasswordFailed');
            $passwordAlert.classList.remove('d-none');
        }).finally(() => {
            buttons.Stop($passwordSubmit);
        });
    });
}
